import { createAsyncThunk } from '@reduxjs/toolkit';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';
import {
  getTestGraphData,
  seedTest,
  getSeedTest,
  updateSeedTest,
  getAllNotLinkedEmailAccountsInInboxAnalyzer,
  getNotLinkedEmailAccountsCountInInboxAnalyzer,
  getLinkedEmailAccountsInInboxAnalyzer,
  addRemoveLinkedEmailAccountsInInboxAnalyzer,
  draftSeedTest,
  getTestSummaryData,
  getTestList,
  getTestReportGraphData,
  getTestReportSummaryData,
  getTestReportList,
  getTestReportEmails,
  getTestConfig,
  updateTestStatus,
  reRunTest,
  getTestPreviewEmail,
  getTestReportExport,
  getSequenceSubsequenceList,
  getShareableReportData,
  getShareableReportSenderEmailAccounts,
  getSequenceStepList,
  deleteTestReport,
  getSeedTestTemplateReviewSendingLimit,
  getSeedTestTemplateWarningBannerConfig,
  getEmailReputation,
  getSpammyEmailAccounts,
  getSpamAssassinReport,
  getTestReportEsps,
} from './helper/inbox-radar.api';
import {
  CreateUpdateTest,
  DraftSeedTestPayload,
  GetTestListRequestPayload,
  SeedTestPayload,
  TestGraphPayload,
  TestReportExportPayload,
  TestReportListRequestPayload,
  TestReportSummaryPayload,
  GetShareableReportDataPayload,
  GetShareableReportSenderEmailAccountsPayload,
  GetEmailAuthenticationPayload,
  GetSpamAssassinReportPayload,
  UpdateTestStatusRequestPayload,
} from './types';
import {
  SpamAssassinScoreReportFilter,
  TestTemplateAction,
  TestType,
} from './enums/inbox-radar';

export const getTestGraphDataRequest = createAsyncThunk<
  ResponseSuccess,
  TestGraphPayload,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestGraphDataRequest', async (args, thunkAPI) => {
  try {
    return await getTestGraphData(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTestSummaryDataRequest = createAsyncThunk<
  ResponseSuccess,
  TestGraphPayload,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestSummaryDataRequest', async (args, thunkAPI) => {
  try {
    return await getTestSummaryData(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTestListRequest = createAsyncThunk<
  ResponseSuccess,
  GetTestListRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestListRequest', async (args, thunkAPI) => {
  try {
    return await getTestList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateTestStatusRequest = createAsyncThunk<
  ResponseSuccess,
  {
    testTemplateId: string;
    action: TestTemplateAction;
    payload?: UpdateTestStatusRequestPayload;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/updateTestStatusRequest',
  async ({ testTemplateId, action, payload }, thunkAPI) => {
    try {
      return await updateTestStatus(testTemplateId, action, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const reRunTestRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/reRunTestRequest', async (testTemplateId, thunkAPI) => {
  try {
    return await reRunTest(testTemplateId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTestPreviewEmailRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestPreviewEmailRequest', async (seedTestId, thunkAPI) => {
  try {
    return await getTestPreviewEmail(seedTestId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const seedTestRequest = createAsyncThunk<
  ResponseSuccess,
  {
    params?: {
      templateType?: TestType;
    };
    payload?: SeedTestPayload;
  },
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/seedTestRequest', async ({ params, payload }, thunkAPI) => {
  try {
    return await seedTest(params, payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSeedTestRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getSeedTestRequest', async (id, thunkAPI) => {
  try {
    return await getSeedTest(id);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSeedTestTemplateReviewSendingLimitRequest = createAsyncThunk<
  ResponseSuccess,
  { id: string; page: number },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getSeedTestTemplateReviewSendingLimitRequest',
  async ({ id, page }, thunkAPI) => {
    try {
      return await getSeedTestTemplateReviewSendingLimit(id, page);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getSeedTestTemplateWarningBannerConfigRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getSeedTestTemplateWarningBannerConfigRequest',
  async (id, thunkAPI) => {
    try {
      return await getSeedTestTemplateWarningBannerConfig(id);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const updateSeedTestRequest = createAsyncThunk<
  ResponseSuccess,
  { id: string; payload: CreateUpdateTest },
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/updateSeedTestRequest', async ({ id, payload }, thunkAPI) => {
  try {
    return await updateSeedTest(id, payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getAllNotLinkedEmailAccountsInInboxRadarRequest = createAsyncThunk<
  ResponseSuccess,
  {
    testTemplateId: string;
    clientIds: number[];
    tagIds: number[];
    search: string;
    pageNum: number;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getAllNotLinkedEmailAccountsInInboxRadarRequest',
  async ({ testTemplateId, clientIds, search, pageNum, tagIds }, thunkAPI) => {
    try {
      return await getAllNotLinkedEmailAccountsInInboxAnalyzer(
        testTemplateId,
        clientIds,
        search,
        pageNum,
        tagIds,
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getNotLinkedEmailAccountsCountInInboxRadarRequest = createAsyncThunk<
  ResponseSuccess,
  {
    testTemplateId: string;
    search: string;
    clientIds: number[];
    tagIds: number[];
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getNotLinkedEmailAccountsCountInInboxRadarRequest',
  async ({ testTemplateId, search, clientIds, tagIds }, thunkAPI) => {
    try {
      return await getNotLinkedEmailAccountsCountInInboxAnalyzer(
        testTemplateId,
        search,
        clientIds,
        tagIds,
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getLinkedEmailAccountsInInboxAnalyzerRequest = createAsyncThunk<
  ResponseSuccess,
  { testTemplateId: string; pageNum: number },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getLinkedEmailAccountsInInboxAnalyzerRequest',
  async ({ testTemplateId, pageNum }, thunkAPI) => {
    try {
      return await getLinkedEmailAccountsInInboxAnalyzer(
        testTemplateId,
        pageNum,
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const addRemoveLinkedEmailAccountsInInboxAnalyzerRequest = createAsyncThunk<
  ResponseSuccess,
  {
    testTemplateId: string;
    emailAccountIdsToAdd: number[];
    emailAccountIdsToRemove: number[];
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/updateLinkedEmailAccounts',
  async (
    { testTemplateId, emailAccountIdsToAdd, emailAccountIdsToRemove },
    thunkAPI,
  ) => {
    try {
      return await addRemoveLinkedEmailAccountsInInboxAnalyzer(
        testTemplateId,
        emailAccountIdsToAdd,
        emailAccountIdsToRemove,
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const draftSeedTestRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestTemplateId: string; payload: DraftSeedTestPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/draftSeedTestRequest',
  async ({ seedTestTemplateId, payload }, thunkAPI) => {
    try {
      return await draftSeedTest(seedTestTemplateId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTestConfigRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestConfigRequest', async (seedTestId, thunkAPI) => {
  try {
    return await getTestConfig(seedTestId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTestReportGraphDataRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestId: string; payload: TestGraphPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getTestReportGraphDataRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getTestReportGraphData(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTestReportSummaryDataRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestId: string; payload: TestReportSummaryPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getTestReportSummaryDataRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getTestReportSummaryData(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTestReportListRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestId: string; payload: TestReportListRequestPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getTestReportListRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getTestReportList(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTestReportEmailsRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getTestReportEmailsRequest', async (seedTestId, thunkAPI) => {
  try {
    return await getTestReportEmails(seedTestId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTestReportExportRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestId: string; payload: TestReportExportPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getTestReportExportRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getTestReportExport(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getSequenceSubsequenceListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getSequenceSubsequenceList', async (args, thunkAPI) => {
  try {
    return await getSequenceSubsequenceList();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSequenceStepListRequest = createAsyncThunk<
  ResponseSuccess,
  { sequenceId: string },
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getSequenceStepList', async ({ sequenceId }, thunkAPI) => {
  try {
    return await getSequenceStepList(sequenceId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getShareableReportDataRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTemplateId: string; payload: GetShareableReportDataPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getShareableReportDataRequest',
  async ({ seedTemplateId, payload }, thunkAPI) => {
    try {
      return await getShareableReportData(seedTemplateId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getShareableReportSenderEmailAccountsRequest = createAsyncThunk<
  ResponseSuccess,
  {
    seedTemplateId: string;
    payload: GetShareableReportSenderEmailAccountsPayload;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getShareableReportSenderEmailAccountsRequest',
  async ({ seedTemplateId, payload }, thunkAPI) => {
    try {
      return await getShareableReportSenderEmailAccounts(
        seedTemplateId,
        payload,
      );
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const deleteTestReportRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('/deleteTestReportRequest', async (args, thunkAPI) => {
  try {
    return await deleteTestReport(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getEmailReputationRequest = createAsyncThunk<
  ResponseSuccess,
  {
    seedTestId: string;
    payload: GetEmailAuthenticationPayload;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getEmailReputationRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getEmailReputation(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getSpammyEmailAccountsRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('inboxRadar/getSpammyEmailAccountsRequest', async (seedTestId, thunkAPI) => {
  try {
    return await getSpammyEmailAccounts(seedTestId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSpamAssassinReportRequest = createAsyncThunk<
  ResponseSuccess,
  {
    seedTestId: string;
    payload: GetSpamAssassinReportPayload;
    filter?: SpamAssassinScoreReportFilter;
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getSpamAssassinReportRequest',
  async (
    { seedTestId, payload, filter = SpamAssassinScoreReportFilter.All },
    thunkAPI,
  ) => {
    try {
      return await getSpamAssassinReport(seedTestId, payload, filter);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTestReportEspsRequest = createAsyncThunk<
  ResponseSuccess,
  { seedTestId: string; payload: TestReportSummaryPayload },
  { rejectValue: ResponseErrorWithHandled }
>(
  'inboxRadar/getTestReportEspsRequest',
  async ({ seedTestId, payload }, thunkAPI) => {
    try {
      return await getTestReportEsps(seedTestId, payload);
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);
